import { PricingRuleSet } from "../../coverage-types";

// Largest cart total whose bracket-priced coverage charge would still land at
// or below `maxCoveragePrice`. Returns `null` when no cap exists (no path can
// exceed max — e.g. all brackets stay under max across their full domain), or
// `undefined` when inputs aren't usable yet (zero/missing maxCoveragePrice).
export function getMaxCartTotalPriceByMaxCoveragePrice({
  maxCoveragePrice,
  backupPercentage,
  pricingRuleSet,
}: {
  maxCoveragePrice: number;
  backupPercentage: number;
  pricingRuleSet: PricingRuleSet[] | undefined;
}): number | null | undefined {
  if (!Number.isFinite(maxCoveragePrice) || maxCoveragePrice <= 0) {
    return undefined;
  }

  const domestic = pricingRuleSet?.[0];
  if (!domestic?.priceBrackets?.length) {
    if (!backupPercentage || backupPercentage <= 0) {
      return null;
    }
    return maxCoveragePrice / backupPercentage;
  }

  const brackets = domestic.priceBrackets
    .map((b) => ({ value: Number(b.value), pricePoint: Number(b.pricePoint) }))
    .filter((b) => Number.isFinite(b.value) && Number.isFinite(b.pricePoint))
    .sort((a, b) => a.pricePoint - b.pricePoint);

  if (!brackets.length) {
    return undefined;
  }

  if (domestic.type === "fixed") {
    for (const [i, bracket] of brackets.entries()) {
      if (bracket.value > maxCoveragePrice) {
        return i === 0 ? 0 : bracket.pricePoint;
      }
    }
    return null;
  }

  for (let i = 0; i < brackets.length; i++) {
    const current = brackets[i]!;
    const next = brackets[i + 1];
    const rate = current.value;
    if (rate <= 0) {
      if (!next) {
        return null;
      }
      continue;
    }
    const capInBracket = maxCoveragePrice / rate;
    if (i > 0 && capInBracket <= current.pricePoint) {
      // Rate stepped up enough that no cart in this bracket fits under
      // maxCoveragePrice; the prior bracket's upper boundary is the ceiling.
      return current.pricePoint;
    }
    if (!next) {
      return capInBracket;
    }
    if (capInBracket >= next.pricePoint) {
      // Cap extends past this bracket — the next bracket may allow a higher
      // cart total (if its rate is lower).
      continue;
    }
    return capInBracket;
  }
  return null;
}

// Bracket-priced coverage cost for a given cart total. Returns the unclamped
// per-bracket result so callers can decide how to handle it relative to
// maxCoveragePrice. Returns `undefined` when there's no usable pricing source.
export function getMaxCoveragePriceByCartTotal({
  cartTotal,
  backupPercentage,
  pricingRuleSet,
}: {
  cartTotal: number;
  backupPercentage: number;
  pricingRuleSet: PricingRuleSet[] | undefined;
}): number | undefined {
  if (!Number.isFinite(cartTotal) || cartTotal <= 0) {
    return undefined;
  }

  const domestic = pricingRuleSet?.[0];
  if (!domestic?.priceBrackets?.length) {
    if (!backupPercentage || backupPercentage <= 0) {
      return undefined;
    }
    return cartTotal * backupPercentage;
  }

  const brackets = domestic.priceBrackets
    .map((b) => ({ value: Number(b.value), pricePoint: Number(b.pricePoint) }))
    .filter((b) => Number.isFinite(b.value) && Number.isFinite(b.pricePoint))
    .sort((a, b) => a.pricePoint - b.pricePoint);

  if (!brackets.length) {
    return undefined;
  }

  let applicable = brackets[0]!;
  for (const bracket of brackets) {
    if (cartTotal >= bracket.pricePoint) {
      applicable = bracket;
    } else {
      break;
    }
  }

  return domestic.type === "fixed"
    ? applicable.value
    : applicable.value * cartTotal;
}
